import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components/macro';

import TrackerLine from './TrackerLine';
import Hitbox from './Hitbox';
import TrackerTooltipItem, {
  TooltipContainer,
  TooltipLabel,
} from 'components/data-visualization/LineChart/TrackerTooltip/TrackerTooltipItem';

import colors from 'styles/themes/colors';

const TooltipContainerWrap = styled('div')<any>`
  ${TooltipContainer} {
    flex-direction: row;
    align-items: center;
    ${({ columnOriented }): string =>
      columnOriented &&
      'min-width: 80px; flex-direction: column; align-items: baseline;'}

    ${TooltipLabel} {
      ${({ columnOriented }): string =>
        columnOriented
          ? 'width: 80px; white-space: pre-line !important; text-align: left; margin-right: 0;'
          : 'margin-right: 1em;'}
      font-size: 11px;
    }
  }
`;

const Trackers: React.FC<any> = (props) => {
  const {
    color,
    entityId,
    hasNoData,
    labels,
    columnOrientedChartLabel,
    margin,
    onPeriodChange,
    onTempPeriodChange,
    parentHeight,
    parentWidth,
    playlistMetrics,
    pointersYValues,
    selectedEntity,
    selectedTempEntity,
    tempPointersYValues,
    tooltipRoot,
    widthLeftLabel,
    xScaleTotal,
  } = props;

  if (!xScaleTotal) return <g />;

  const tooltipLeft =
    xScaleTotal(selectedTempEntity?.periodKey) + margin.left + widthLeftLabel;
  const valueColor = colors.NightBlue;
  const pathPlotWidth = parentWidth
    ? parentWidth - margin.left - margin.right - widthLeftLabel
    : 0;

  return (
    <>
      {/** Constant tracker */}
      {selectedEntity && Object.keys(selectedEntity).length > 0 && (
        <TrackerLine
          color={color}
          date={selectedEntity?.periodKey}
          hasNoData={hasNoData}
          height={
            !hasNoData.stream || !hasNoData.position
              ? parentHeight
              : parentHeight / 2
          }
          margin={margin}
          width={pathPlotWidth}
          widthLeftLabel={widthLeftLabel}
          xCoord={xScaleTotal(selectedEntity?.periodKey)}
          yData={pointersYValues}
        />
      )}
      {/** Temporary tracker */}
      {selectedTempEntity && Object.keys(selectedTempEntity).length > 0 && (
        <TrackerLine
          color={color}
          date={selectedTempEntity?.periodKey}
          hasNoData={hasNoData}
          height={
            !hasNoData.stream || !hasNoData.position
              ? parentHeight
              : parentHeight / 2
          }
          isGhostTracker={true}
          margin={margin}
          width={pathPlotWidth}
          widthLeftLabel={widthLeftLabel}
          xCoord={xScaleTotal(selectedTempEntity?.periodKey)}
          yData={tempPointersYValues}
        />
      )}
      {/** Click and hover trigger */}
      <g transform={`translate(${margin.left + widthLeftLabel}, 10)`}>
        <Hitbox
          handleMouseClick={onPeriodChange}
          handleMouseHover={onTempPeriodChange}
          size={{
            width: pathPlotWidth,
            height: parentHeight,
          }}
          x={xScaleTotal}
        />
        {/** Tooltips */}
        {tooltipRoot &&
          tooltipRoot.current &&
          selectedTempEntity &&
          createPortal(
            <>
              {playlistMetrics.length > 0 &&
                playlistMetrics.map(({ fieldName, metric, top }: any) =>
                  selectedTempEntity[fieldName] !== null &&
                  selectedTempEntity.hasOwnProperty(fieldName) ? (
                    <TooltipContainerWrap
                      key={`tooltip-${entityId}-${metric}`}
                      columnOriented={columnOrientedChartLabel}
                    >
                      <TrackerTooltipItem
                        tooltipRoot={tooltipRoot}
                        left={tooltipLeft}
                        top={top}
                        color={valueColor}
                        labels={[
                          labels && labels[metric] ? labels[metric] : metric,
                        ]}
                        yValue={selectedTempEntity[fieldName]}
                        isFormattedNumber
                      />
                    </TooltipContainerWrap>
                  ) : null
                )}
            </>,
            tooltipRoot.current
          )}
      </g>
      )
    </>
  );
};

export default Trackers;
